import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react'

export type FontSizeToken =
	| 'eventTitle'
	| 'eventTime'
	| 'dayDate'
	| 'dayName'
	| 'dayStats'
	| 'timeGutter'
	| 'toolbarTitle'
	| 'noteText'
	| 'noteTime'
	| 'dialogTitle'
	| 'dialogLabel'
	| 'dialogInput'
	| 'dialogInputHeight'

export type FontSizes = Record<FontSizeToken, number>

export const FONT_TOKENS: FontSizeToken[] = [
	'eventTitle',
	'eventTime',
	'dayDate',
	'dayName',
	'dayStats',
	'timeGutter',
	'toolbarTitle',
	'noteText',
	'noteTime',
	'dialogTitle',
	'dialogLabel',
	'dialogInput',
]

export const CONTROL_TOKENS: FontSizeToken[] = ['dialogInputHeight']

export const DEFAULT_FONT_SIZES: FontSizes = {
	eventTitle: 14,
	eventTime: 14,
	dayDate: 18,
	dayName: 14,
	dayStats: 14,
	timeGutter: 12,
	toolbarTitle: 20,
	noteText: 12,
	noteTime: 10,
	dialogTitle: 16,
	dialogLabel: 14,
	dialogInput: 14,
	dialogInputHeight: 40,
}

export const FONT_SIZE_RANGES: Record<FontSizeToken, { min: number; max: number }> = {
	eventTitle: { min: 11, max: 20 },
	eventTime: { min: 11, max: 18 },
	dayDate: { min: 14, max: 28 },
	dayName: { min: 11, max: 20 },
	dayStats: { min: 11, max: 20 },
	timeGutter: { min: 10, max: 16 },
	toolbarTitle: { min: 14, max: 28 },
	noteText: { min: 10, max: 16 },
	noteTime: { min: 8, max: 14 },
	dialogTitle: { min: 13, max: 22 },
	dialogLabel: { min: 11, max: 18 },
	dialogInput: { min: 11, max: 20 },
	dialogInputHeight: { min: 32, max: 56 },
}

const CSS_VAR_NAMES: Record<FontSizeToken, string> = {
	eventTitle: '--font-size-event-title',
	eventTime: '--font-size-event-time',
	dayDate: '--font-size-day-date',
	dayName: '--font-size-day-name',
	dayStats: '--font-size-day-stats',
	timeGutter: '--font-size-time-gutter',
	toolbarTitle: '--font-size-toolbar-title',
	noteText: '--font-size-note-text',
	noteTime: '--font-size-note-time',
	dialogTitle: '--font-size-dialog-title',
	dialogLabel: '--font-size-dialog-label',
	dialogInput: '--font-size-dialog-input',
	dialogInputHeight: '--input-height-dialog',
}

export function cssVarFor(token: FontSizeToken): string {
	return `var(${CSS_VAR_NAMES[token]})`
}

const STORAGE_KEY = 'fontSizes'

interface FontSizeContextValue {
	sizes: FontSizes
	setSize: (token: FontSizeToken, value: number) => void
	reset: () => void
}

const FontSizeContext = createContext<FontSizeContextValue | null>(null)

function clampSize(token: FontSizeToken, value: number): number {
	const { min, max } = FONT_SIZE_RANGES[token]
	if (Number.isNaN(value)) return DEFAULT_FONT_SIZES[token]
	return Math.max(min, Math.min(max, Math.round(value)))
}

function sanitize(stored: unknown): FontSizes {
	if (!stored || typeof stored !== 'object') return DEFAULT_FONT_SIZES
	const next: FontSizes = { ...DEFAULT_FONT_SIZES }
	for (const token of Object.keys(DEFAULT_FONT_SIZES) as FontSizeToken[]) {
		const raw = (stored as Record<string, unknown>)[token]
		if (typeof raw === 'number') {
			next[token] = clampSize(token, raw)
		}
	}
	return next
}

function loadInitial(): FontSizes {
	if (typeof window === 'undefined') return DEFAULT_FONT_SIZES
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return DEFAULT_FONT_SIZES
		return sanitize(JSON.parse(raw))
	} catch {
		return DEFAULT_FONT_SIZES
	}
}

function applyToRoot(sizes: FontSizes) {
	if (typeof document === 'undefined') return
	const root = document.documentElement
	for (const token of Object.keys(sizes) as FontSizeToken[]) {
		root.style.setProperty(CSS_VAR_NAMES[token], `${sizes[token]}px`)
	}
}

export function FontSizeProvider({ children }: { children: ReactNode }) {
	const [sizes, setSizes] = useState<FontSizes>(loadInitial)

	useEffect(() => {
		applyToRoot(sizes)
		try {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes))
		} catch {
			// Quota exceeded or storage disabled — silently ignore.
		}
	}, [sizes])

	const setSize = useCallback((token: FontSizeToken, value: number) => {
		setSizes((prev) => {
			const clamped = clampSize(token, value)
			if (prev[token] === clamped) return prev
			return { ...prev, [token]: clamped }
		})
	}, [])

	const reset = useCallback(() => {
		setSizes(DEFAULT_FONT_SIZES)
	}, [])

	const value = useMemo<FontSizeContextValue>(
		() => ({ sizes, setSize, reset }),
		[sizes, setSize, reset],
	)

	return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>
}

export function useFontSizes(): FontSizeContextValue {
	const context = useContext(FontSizeContext)
	if (!context) {
		throw new Error('useFontSizes must be used within a FontSizeProvider')
	}
	return context
}
